import React from 'react';
import { Helmet } from 'react-helmet';
import DetailsHeaderText from '../components/DetailsHeaderText';
import driveImage2 from '../assets/images/drive/drive_2.png';
import driveImage3 from '../assets/images/drive/drive_3.png';
import driveImage4 from '../assets/images/drive/drive_4.png';
import driveImage5 from '../assets/images/drive/drive_5.png';
import driveImage6 from '../assets/images/drive/drive_6.png';
import driveImage7 from '../assets/images/drive/drive_7.png';
import driveImage8 from '../assets/images/drive/drive_8.png';
import driveImage9 from '../assets/images/drive/drive_9.png';
import driveImage10 from '../assets/images/drive/drive_10.png';
import driveImage11 from '../assets/images/drive/drive_11.png';
import TextWithImageSection from '../components/TextWithImageSection';
import Subtitle from '../components/Subtitle';
import Text from '../components/Text';
import Media from '../components/Media';
import SectionDetailsLink from '../components/SectionDetailsLink';
import { sectionIds } from './utils';
import DividerTitle from '../components/DividerTitle';
import Title from '../components/Title';
import FeedbackCarousel from '../components/FeedbackCarousel';
import DetailsText from '../components/DetailsText';
import { routes } from '../routes';
import PageFooterSlope from './PageFooterSlope';
import SectionTitle from '../components/SectionTitle';
import TextWithImageSectionScenarios from '../components/TextWithImageSectionScenarios';

const DriveDetailsContent = () => (
    <>
        <Helmet>
            <title>realCity drive - tablet based on-board unit</title>
            <meta
                name="description"
                content="Enhance your fleet with a portable, tablet-based on-board unit. Easy installation, real-time dispatch integration, and a user-friendly interface for seamless navigation."
            />
            <meta
                name="keywords"
                content="real-time vehicle tracking, fleet, management system, public transport technology, IP-based vehicle integration, driver assistance system"
            />
        </Helmet>
        <DetailsHeaderText
            category="Products"
            title="realCity | drive"
        />
        <TextWithImageSection
            introductorySection
            text={(
                <>
                    <Subtitle>Portable On-Board Unit for Smarter Transit Operations</Subtitle>
                    <Text>
                        Install in minutes. Navigate with confidence. Up and running in no time. Built for modern
                        transport operations with map-based navigation, real-time communication, and effortless
                        installation.
                    </Text>
                    <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                        Book a short intro call
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage2}
                    variant="left"
                    alt="Portable On-Board Unit for Smarter Transit Operations"
                />
            )}
            alignment="image-right"
        />
        <DividerTitle>
            realCity | drive - Simple To Deploy, Powerful In Operation
        </DividerTitle>
        <TextWithImageSection
            text={(
                <>
                    <Title>Quick installation</Title>
                    <Text>
                        realCity | drive is an Android-based software that is compatible with a wide range of
                        on-board hardware, enabling flexible integration into existing vehicle systems as well as
                        deployment on portable, mobile on-board units. No complex wiring or vehicle-specific
                        modifications are required, keeping setup time and operational disruption to a minimum.
                        Devices can be installed and commissioned in a matter of hours, supporting fast fleet
                        rollout and straightforward replacement or relocation when needed.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage3}
                    variant="right"
                    alt="Quick installation"
                />
            )}
            alignment="image-left"
            first
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Driver-friendly UI</Title>
                    <Text>
                        The interface was designed with drivers’ real-world needs in mind. Because it is often used just
                        before starting a shift, during short breaks, or when changing routes mid-day, the UI had to be
                        clear, intuitive, and map-based to support quick navigation. The focus is on fast access to
                        relevant information, minimal interaction steps, and reduced cognitive load in time-constrained
                        operational situations.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage4}
                    variant="left"
                    alt="Driver-friendly UI"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Always in sync with the control center</Title>
                    <Text>
                        Along with integration to already deployed traffic management software, realCity | drive works
                        best with realCity | dispatch, a browser-based control application used by operations staff. Any
                        operational changes made in the control interface - such as route updates or service adjustments
                        - are automatically delivered to the on-board unit, ensuring drivers always work with the latest
                        information.
                    </Text>
                    <SectionDetailsLink navigateTo={routes.dispatchProduct}>
                        Explore realcity | Dispatch
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage5}
                    variant="right"
                    alt="Always in sync with the control center"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Connection management</Title>
                    <Text>
                        realCity | drive supports reliable connection handling by providing drivers with automatic
                        notifications about relevant transfers and connecting services. Drivers can continuously track
                        the status and position of connecting vehicles directly on the map, ensuring full visibility of
                        approaching services. Any dispatcher interventions are immediately communicated to the on-board
                        unit, drawing the driver’s attention without delay. This ensures that connection-related
                        situations are handled in a coordinated and timely manner.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage6}
                    variant="left"
                    alt="Connection management"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Demand-responsive transport integration</Title>
                    <Text>
                        realCity | drive natively supports demand-responsive transport operations by presenting all
                        relevant trip and passenger information directly on the driver interface. The system provides
                        dynamically updated route guidance based on incoming demand, allowing drivers to simply follow
                        the route shown on the map. Drivers can see how many passengers are expected to board or alight
                        at each stop, with updates reflected immediately as bookings change. The interface also enables
                        on-the-spot passenger management, supporting real-time operational flexibility.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage7}
                    variant="right"
                    alt="Demand-responsive transport integration"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Passenger information integration</Title>
                    <Text>
                        By continuously transmitting vehicle data, realCity | drive enables accurate, real-time
                        passenger information across all channels. Location and delay status are processed centrally and
                        distributed to passenger-facing systems within seconds. This ensures that travellers receive
                        consistent and up-to-date information, whether via mobile applications, displays, or other
                        interfaces. The result is improved transparency and a more predictable travel experience for
                        passengers.
                    </Text>
                    <SectionDetailsLink navigateTo={routes.rideProduct}>
                        Discover realcity | Ride
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage8}
                    variant="left"
                    alt="Demand-responsive transport integration"
                />
            )}
            alignment="image-right"
        />
        <DividerTitle>
            Flexible use across vehicles and service scenarios
        </DividerTitle>
        <TextWithImageSectionScenarios
            text={(
                <>
                    <Title>Deploy extra buses without a second thought</Title>
                    <Text>
                        During peak seasons or when unexpected replacements are needed, you don’t have to stress about
                        expensive on-board hardware upgrades. With realCity | drive, drivers can use a tablet or even
                        their own mobile phones with no major vehicle modifications. This flexibility makes it easy to
                        put additional buses into service on short notice.
                        The mobile on-board-units deliver the same functionality as the integrated hardware, ensuring
                        smooth operations and uninterrupted passenger service.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage9}
                    variant="center"
                    alt="Deploy extra buses without a second thought"
                />
            )}
        />
        <TextWithImageSectionScenarios
            text={(
                <>
                    <Title>Protect your vintage vehicles, while delivering modern service quality</Title>
                    <Text>
                        Historic vehicles rolling through the old town are more than just tourist attractions, they’re a
                        piece of urban history. In Budapest, nostalgia rides bring a unique travel experience every
                        summer, serving both locals and visitors. Since these vehicles are not in constant use and often
                        rotate in service, mobile on-board units are the perfect solution. A single tablet can be moved
                        between vehicles, providing drivers with map-based navigation and real-time updates, and making
                        historic vehicles visible to passengers in route planners—all without permanent, costly, or
                        invasive installation.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage10}
                    variant="center"
                    alt="Protect your vintage vehicles, while delivering modern service quality"
                />
            )}
        />
        <TextWithImageSectionScenarios
            text={(
                <>
                    <Title>Fully integrated operations with the whole realCity software portfolio</Title>
                    <Text>
                        In cities like Szombathely and Várpalota, realCity | drive shows how software and on-board
                        hardware can work together to improve operations, driver and passenger experience. Tablets and
                        devices in vehicles handle automatic or driver-initiated audio announcements, external and
                        inside display updates and custom content, ticketing feedback, and passenger counting data.
                        Vehicle diagnostics - especially for electric buses - allow operators to monitor battery health,
                        energy use, and other critical e-bus data in real time.
                        In the control center, cloud-based tools for planning, monitoring, and analysis give operators
                        full visibility of the fleet. Any changes - schedule updates, route adjustments, or service
                        alerts - are pushed automatically to vehicles, keeping drivers informed and passengers up to
                        date.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={driveImage11}
                    variant="center"
                    alt="Fully integrated operations with the whole realCity software portfolio"
                />
            )}
        />
        <FeedbackCarousel items={[
            // eslint-disable-next-line max-len
            'It took me about half an hour to learn how to use the app. The features are easy to navigate, and the main screen already has everything I usually need.',
            'The map helps me avoid rushing with clear routes, colour-coded alerts, and sound signals.',
            // eslint-disable-next-line max-len
            'The app is a huge help in my daily work. It makes communication with dispatchers easier, helps with navigation, and keeps travel times on track.',
        ]}
        />
        <DetailsText>
            <SectionTitle>Let’s talk about how realCity | drive could fit your fleet!</SectionTitle>
            <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                Book a short intro call
            </SectionDetailsLink>
        </DetailsText>
        <PageFooterSlope />
    </>
);

export default DriveDetailsContent;
